import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  Badge,
  Divider,
  Link,
  FormControl,
  FormLabel,
  useDisclosure,
} from '@chakra-ui/react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'
import Timeline from '@/components/helpdesk/TicketDetail/Timeline'
import TicketUpdateModal, { type TicketUpdateItem } from '@/components/helpdesk/TicketDetail/TicketUpdateModal'

const ticketPath = '/ticket-management/all-tickets'
const statusesPath = '/service-catalog/ticket-statuses'

/** Status codes that must not appear in the Update Status dropdown (approval/workflow-only). */
const EXCLUDED_STATUS_CODES = ['approved', 'for_approval', 'new', 'assigned', 'rejected']

type TicketDetail = {
  id: number
  request_number: string
  description: string | null
  contact_number: string | null
  contact_name: string | null
  contact_email: string | null
  ticket_status_id: number
  ticket_status_label?: string
  ticket_status?: { id: number; code: string; label: string }
  service_type_name?: string
  service_type?: { id: number; name: string; code: string }
  submitted_at: string | null
  created_at: string | null
  user?: { id: number; user_login: string; user_email: string }
  assigned_to_user?: { id: number; user_login: string; user_email: string }
  sla?: { id: number; severity: string; response_minutes: number; resolution_minutes: number }
  attachment_metadata?: Array<{ name?: string; file_url?: string }> | null
}

type StatusOption = { id: number; code: string; label: string }

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return iso
  }
}

export default function UpdateStatusPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { showError, showSuccess } = useToastNotification()

  const [ticket, setTicket] = useState<TicketDetail | null>(null)
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([])
  const [selectedStatusId, setSelectedStatusId] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [ticketUpdates, setTicketUpdates] = useState<TicketUpdateItem[]>([])
  const { isOpen: isUpdateOpen, onOpen: onUpdateOpen, onClose: onUpdateClose } = useDisclosure()

  const ticketId = id ? Number(id) : undefined

  const fetchTicket = useCallback(async () => {
    if (!ticketId || Number.isNaN(ticketId)) return
    try {
      const { data } = await apiClient.get(`${ticketPath}/${ticketId}`)
      const t = data.data ?? data
      setTicket(t)
      setSelectedStatusId(t.ticket_status_id ?? null)
    } catch {
      setFetchError('Failed to load ticket')
    }
  }, [ticketId])

  const fetchStatuses = useCallback(async () => {
    try {
      const { data } = await apiClient.get(statusesPath, { params: { per_page: 100 } })
      const list = data.data ?? data
      if (Array.isArray(list)) {
        const excluded = new Set(EXCLUDED_STATUS_CODES.map((c) => c.toLowerCase()))
        setStatusOptions(
          list
            .map((s: { id: number; code: string; label?: string }) => ({
              id: s.id,
              code: (s.code ?? '').toLowerCase(),
              label: s.label ?? s.code,
            }))
            .filter((s: { code: string }) => !excluded.has(s.code))
        )
      }
    } catch {
      // non-blocking
    }
  }, [])

  const fetchTicketUpdates = useCallback(async () => {
    if (!ticketId || Number.isNaN(ticketId)) return
    try {
      const { data } = await apiClient.get(`${ticketPath}/${ticketId}/updates`, { params: { per_page: 50 } })
      const raw = data.data ?? data
      const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : []
      setTicketUpdates(list)
    } catch {
      // non-blocking
    }
  }, [ticketId])

  useEffect(() => {
    if (!ticketId || Number.isNaN(ticketId)) {
      navigate(ticketPath, { replace: true })
      return
    }
    setLoading(true)
    setFetchError(null)
    Promise.all([fetchTicket(), fetchStatuses()]).finally(() => setLoading(false))
  }, [ticketId, navigate, fetchTicket, fetchStatuses])

  useEffect(() => {
    if (ticketId && !Number.isNaN(ticketId)) fetchTicketUpdates()
  }, [ticketId, fetchTicketUpdates])

  const formatTimelineDate = (iso: string) =>
    new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })

  const timelineItems = useMemo(() => {
    const authorName = (u: TicketUpdateItem) => {
      const a = u.author
      if (a?.full_name) return a.full_name
      if (a?.first_name || a?.last_name) return [a.first_name, a.last_name].filter(Boolean).join(' ').trim()
      if (a?.user_login) return a.user_login
      return 'Agent'
    }
    return [...ticketUpdates]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .map((u) => ({
        id: `update-${u.id}`,
        actor: authorName(u),
        action: u.content ?? '',
        timeLabel: `${formatTimelineDate(u.created_at)} · by ${authorName(u)}`,
        type: 'update' as const,
      }))
  }, [ticketUpdates])

  const handleUpdateStatus = async () => {
    if (!ticketId || selectedStatusId == null) return
    setSaving(true)
    try {
      await apiClient.put(`${ticketPath}/${ticketId}`, { ticket_status_id: selectedStatusId })
      showSuccess('Success', 'Status updated')
      setTicket((prev) =>
        prev
          ? {
              ...prev,
              ticket_status_id: selectedStatusId,
              ticket_status_label: statusOptions.find((s) => s.id === selectedStatusId)?.label ?? prev.ticket_status_label,
            }
          : null
      )
      fetchTicketUpdates()
    } catch (err) {
      showError(err as Error)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <AdminLayout>
        <Box p={6} display="flex" alignItems="center" justifyContent="center" minH="200px">
          <Spinner size="lg" />
          <Text ml={3}>Loading ticket...</Text>
        </Box>
      </AdminLayout>
    )
  }

  if (fetchError || !ticket) {
    return (
      <AdminLayout>
        <Box p={6}>
          <Card>
            <CardBody>
              <Text color={colors.textSecondary}>{fetchError ?? 'Ticket not found.'}</Text>
              <Button variant="link" colorScheme="blue" mt={2} onClick={() => navigate(ticketPath)}>
                Back to All Tickets
              </Button>
            </CardBody>
          </Card>
        </Box>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <Box
        bg={colors.bg}
        p={{ base: 4, md: 6 }}
        w="100%"
        minW={0}
        minH="calc(100vh - 80px - 48px)"
        display="flex"
        flexDir="column"
      >
        <Button
          variant="ghost"
          leftIcon={<ChevronLeft size={18} />}
          onClick={() => navigate(ticketPath)}
          alignSelf="flex-start"
          mb={5}
        >
          Go Back
        </Button>

        <Heading size="md" mb={2}>
          Update ticket status
        </Heading>
        <Text fontSize="sm" color={colors.text} mb={8}>
          {ticket.request_number} — Change status and save. Ticket details are read-only.
        </Text>

        <Flex gap={8} align="stretch" flex="1" minH={0} direction={{ base: 'column', lg: 'row' }}>
          {/* Ticket details (read-only) */}
          <Box flex="1" minW={0} display="flex" flexDir="column">
            <Card>
              <CardHeader pb={2}>
                <Heading size="sm">Ticket details</Heading>
              </CardHeader>
              <CardBody pt={2} pb={6}>
                <VStack align="stretch" spacing={5}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                    <Box>
                      <Text fontSize="xs" color={colors.textMuted} mb={1.5}>
                        Request #
                      </Text>
                      <Text fontWeight="medium">{ticket.request_number}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color={colors.textMuted} mb={1.5}>
                        Current status
                      </Text>
                      <Badge colorScheme="blue">
                        {ticket.ticket_status?.label ?? ticket.ticket_status_label ?? '—'}
                      </Badge>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color={colors.textMuted} mb={1.5}>
                        Service type
                      </Text>
                      <Text>{ticket.service_type?.name ?? ticket.service_type_name ?? '—'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color={colors.textMuted} mb={1.5}>
                        Submitted at
                      </Text>
                      <Text>{formatDate(ticket.submitted_at)}</Text>
                    </Box>
                  </SimpleGrid>

                  <Box>
                    <Text fontSize="xs" color={colors.textMuted} mb={1.5}>
                      Description
                    </Text>
                    <Text
                      whiteSpace="pre-wrap"
                      bg={colors.sectionBg}
                      p={4}
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor={colors.borderLight}
                    >
                      {ticket.description || '—'}
                    </Text>
                  </Box>

                  <Divider my={1} />
                  <Text fontWeight="semibold" fontSize="sm" mt={1}>
                    Contact
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                    <Box>
                      <Text fontSize="xs" color={colors.textMuted} mb={1.5}>
                        Name
                      </Text>
                      <Text>{ticket.contact_name || '—'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color={colors.textMuted} mb={1.5}>
                        Email
                      </Text>
                      <Text>{ticket.contact_email || '—'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color={colors.textMuted} mb={1.5}>
                        Phone
                      </Text>
                      <Text>{ticket.contact_number || '—'}</Text>
                    </Box>
                  </SimpleGrid>

                  <Divider my={1} />
                  <Text fontWeight="semibold" fontSize="sm" mt={1}>
                    Assignment
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                    <Box>
                      <Text fontSize="xs" color={colors.textMuted} mb={1.5}>
                        Submitted by
                      </Text>
                      <Text>
                        {ticket.user ? `${ticket.user.user_login} (${ticket.user.user_email})` : '—'}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color={colors.textMuted} mb={1.5}>
                        Assigned to
                      </Text>
                      <Text>
                        {ticket.assigned_to_user
                          ? `${ticket.assigned_to_user.user_login} (${ticket.assigned_to_user.user_email})`
                          : '—'}
                      </Text>
                    </Box>
                  </SimpleGrid>

                  {ticket.attachment_metadata && ticket.attachment_metadata.length > 0 && (
                    <>
                      <Divider my={1} />
                      <Text fontWeight="semibold" fontSize="sm" mt={1}>
                        Attachments
                      </Text>
                      <VStack align="stretch" spacing={2} pt={1}>
                        {ticket.attachment_metadata.map(
                          (file: { name?: string; file_url?: string }, i: number) => (
                            <Box key={i}>
                              {file.file_url ? (
                                <Link href={file.file_url} isExternal color="blue.500">
                                  {file.name || 'File'}
                                </Link>
                              ) : (
                                <Text>{file.name || 'File'}</Text>
                              )}
                            </Box>
                          )
                        )}
                      </VStack>
                    </>
                  )}
                </VStack>
              </CardBody>
            </Card>
          </Box>

          {/* Status update card + Ticket updates timeline */}
          <Box
            w={{ base: '100%', lg: '380px' }}
            minW={{ base: 0, lg: '380px' }}
            maxW={{ base: '100%', lg: '380px' }}
            flexShrink={0}
            display="flex"
            flexDir="column"
            gap={6}
            minH={0}
          >
            <Card w="100%">
              <CardHeader pb={2}>
                <Heading size="sm">Status</Heading>
              </CardHeader>
              <CardBody pt={2} pb={5}>
                <FormControl>
                  <FormLabel mb={2}>New status</FormLabel>
                  <Select
                    value={selectedStatusId ?? ''}
                    onChange={(e) => setSelectedStatusId(e.target.value ? Number(e.target.value) : null)}
                    placeholder="Select status"
                  >
                    {statusOptions.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  mt={5}
                  colorScheme="blue"
                  w="full"
                  onClick={handleUpdateStatus}
                  isLoading={saving}
                  isDisabled={selectedStatusId == null || selectedStatusId === ticket.ticket_status_id}
                >
                  Update status
                </Button>
              </CardBody>
            </Card>

            <Box w="100%" flex="1" minH="220px" display="flex" flexDir="column" overflow="hidden">
              <Timeline
                items={timelineItems}
                onAddComment={onUpdateOpen}
                addCommentLabel="Add comment"
                minH="200px"
                w="100%"
              />
            </Box>
          </Box>
        </Flex>

        <TicketUpdateModal
          isOpen={isUpdateOpen}
          onClose={onUpdateClose}
          ticketId={String(ticketId)}
          ticketNumber={ticket.request_number}
          onSubmitted={fetchTicketUpdates}
        />
      </Box>
    </AdminLayout>
  )
}
